import { useMutation } from "@apollo/client/react";
import { DSCardWrapper } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import mixpanel from "mixpanel-browser";
import { useNavigate } from "react-router";

import DELETE_TEAM from "../../../graphql/mutations/DeleteTeam";
import { useAuth, useTeam } from "../../../hooks";
import { alert } from "../../common/Alert";

export function DeleteTeamModal({ shouldShow, hideModal, team }) {
  const navigate = useNavigate();
  const { userUuid } = useAuth();
  const { refreshUserTeams } = useTeam();
  const { setCurrentTeam, emptyTeam, currentTeam } = useTeam();
  const [deleteTeam, { loading }] = useMutation(DELETE_TEAM);

  const isOnlyMember =
    team?.users?.length === 1 && team?.users[0]?.uuid === userUuid;

  const handleDeleteTeam = async () => {
    if (!isOnlyMember) return;

    try {
      const { data } = await deleteTeam({
        variables: {
          teamUuid: team.uuid,
        },
      });

      if (data?.deleteTeam?.ok) {
        alert("success", "Team deleted successfully.");
        mixpanel.track("Team Deleted", {
          teamUuid: team.uuid,
          teamName: team.name,
        });
        if (currentTeam?.uuid === team.uuid) {
          setCurrentTeam(emptyTeam);
        }
        refreshUserTeams();
      } else {
        alert(
          "error",
          data?.deleteTeam?.message || "Failed to delete team.",
        );
      }
      navigate("/teams");
    } catch (error) {
      alert("error", "Failed to delete team. Please try again.");
      console.error(error);
      mixpanel.track("Delete Team Error", {
        teamUuid: team.uuid,
        error: error.message,
      });
    }
  };

  return (
    <DSModal
      isOpen={shouldShow}
      onClose={hideModal}
      title="Delete Team"
      dataTestId="delete-team-modal"
      actions={[
        {
          label: "Cancel",
          variant: "cancel",
          onClick: hideModal,
        },
        {
          label: "Delete",
          variant: "primary",
          onClick: handleDeleteTeam,
          disabled: loading || !isOnlyMember,
          dataTestId: "confirm-button",
        },
      ]}
    >
      <div>
        {isOnlyMember ? (
          <>
            <h5>
              Are you sure you want to delete{" "}
              <strong>{team?.name}</strong>? This action cannot be
              undone.
            </h5>
            <div className="my-md">
              <DSCardWrapper
                variant="danger"
                padding="p-sm"
                dataTestId="delete-team-danger-alert"
              >
                This will permanently delete the team and all
                associated data.
              </DSCardWrapper>
            </div>
          </>
        ) : (
          <>
            <h5>
              You cannot delete <strong>{team?.name}</strong> while
              there are other team members.
            </h5>
            <div className="my-md">
              <DSCardWrapper
                variant="warning"
                padding="p-sm"
                dataTestId="delete-team-warning-alert"
              >
                To delete this team, you must first remove all other
                team members. Once you are the only member, you can
                delete the team.
              </DSCardWrapper>
            </div>
          </>
        )}
      </div>
    </DSModal>
  );
}
